import React, { useState, useEffect } from "react";
import { Pencil } from "lucide-react";
import { getFixedCosts, createFixedCost, updateFixedCost } from "../../../services/AdminPortal/financeAPI";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";

ChartJS.register(ArcElement, Tooltip, Legend);

const FixedCosts = ({ onDataChange }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [sourceName, setSourceName] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [timePeriod, setTimePeriod] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Generate colors for pie chart
  const generateColors = (count) => {
    const colors = ['#FFCA20', '#4ADE80', '#FF6B6B', '#60A5FA', '#A78BFA', '#F59E0B'];
    return colors.slice(0, count);
  };

  const chartData = {
    labels: data.map(item => item.payment),
    datasets: [{
      data: data.map(item => item.amount),
      backgroundColor: generateColors(data.length),
      borderWidth: 0,
      hoverOffset: 8,
    }]
  };

  const chartOptions = {
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function(context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percent = ((context.raw / total) * 100).toFixed(1);
            return `${context.label}: ₹${context.raw.toLocaleString('en-IN')} (${percent}%)`;
          }
        }
      }
    },
    maintainAspectRatio: false,
    responsive: true,
  };

  useEffect(() => {
    fetchFixedCosts();
  }, []);

  const fetchFixedCosts = async () => {
    try {
      const fixedCosts = await getFixedCosts();
      setData(fixedCosts);
    } catch (error) {
      console.error("Error fetching fixed costs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!sourceName || !amount) return;
    
    try {
      const payload = {
        payment: sourceName,
        description,
        amount: parseFloat(amount),
        timePeriod: timePeriod || "Monthly",
      };

      if (editingItem) {
        await updateFixedCost(editingItem._id, payload);
      } else {
        await createFixedCost(payload);
      }
      
      setShowModal(false);
      resetForm();
      await fetchFixedCosts();
      if (onDataChange) onDataChange();
    } catch (error) {
      console.error("Error saving fixed cost:", error);
    }
  };

  const resetForm = () => {
    setSourceName("");
    setDescription("");
    setAmount("");
    setTimePeriod("");
    setEditingItem(null);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setSourceName(item.payment);
    setDescription(item.description || "");
    setAmount(item.amount.toString());
    setTimePeriod(item.timePeriod || "Monthly");
    setShowModal(true);
  };

  const total = data.reduce((sum, item) => sum + item.amount, 0);

  return (
    <div className="bg-[#131313] border border-[#333] p-4 rounded-lg w-full relative mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white text-sm font-semibold">FIXED COSTS</h2>
        <button
          onClick={() => setShowModal(true)}
          className="bg-yellow-400 hover:bg-yellow-300 text-black text-xs font-semibold px-3 py-1 rounded flex items-center gap-1"
        >
          + Add Fixed Cost
        </button>
      </div>

      {loading ? (
        <div className="text-center text-gray-400 py-8">Loading...</div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Pie Chart */}
          <div className="w-80 h-64 shrink-0">
            {data.length > 0 ? (
              <Pie data={chartData} options={chartOptions} />
            ) : (
              <div className="w-32 h-32 rounded-full bg-[#1f1f1f] mx-auto flex items-center justify-center text-gray-400 text-xs">
                No Data
              </div>
            )}
          </div>

          {/* Table */}
          <div className="w-full overflow-x-auto">
            <table className="min-w-full text-[12px] text-gray-200">
              <thead>
                <tr className="text-gray-400 text-[11px]">
                  <th className="text-left pb-2">PAYMENT</th>
                  <th className="pb-2 text-center">DESCRIPTION</th>
                  <th className="text-right pb-2">AMOUNT</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item, idx) => (
                  <tr key={item._id || idx} className="border-t border-[#2a2a2a]">
                    <td className="py-1">{item.payment}</td>
                    <td className="py-1 text-center">{item.description || "-"}</td>
                    <td className="text-right py-1 pr-1 flex justify-end items-center gap-1">
                      ₹{item.amount.toLocaleString("en-IN")}
                      <Pencil 
                        size={12} 
                        className="text-yellow-300 ml-3 cursor-pointer" 
                        onClick={() => handleEdit(item)}
                      />
                    </td>
                  </tr>
                ))}
                {data.length === 0 && (
                  <tr>
                    <td colSpan="3" className="text-center text-gray-400 py-4">
                      No fixed costs found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-[#131313] p-6 rounded-lg w-full max-w-lg shadow-lg border border-[#333]">
            <h3 className="text-white text-base font-semibold mb-4">
              {editingItem ? "Edit Fixed Cost" : "Add Fixed Cost"}
            </h3>

            <div className="mb-4">
              <label className="text-gray-400 text-sm block mb-1">Payment Name</label>
              <input
                type="text"
                placeholder="Enter payment name"
                value={sourceName}
                onChange={(e) => setSourceName(e.target.value)}
                className="w-full px-3 py-2 rounded bg-[#131313] text-white text-sm border border-[#444] placeholder-gray-500 focus:outline-none"
              />
            </div>

            <div className="mb-4">
              <label className="text-gray-400 text-sm block mb-1">Description (Optional)</label>
              <input
                type="text"
                placeholder="Enter description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 rounded bg-[#131313] text-white text-sm border border-[#444] placeholder-gray-500 focus:outline-none"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mb-5">
              <div className="w-full sm:w-1/2">
                <label className="text-gray-400 text-sm block mb-1">Amount</label>
                <input
                  type="number"
                  placeholder="Enter amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full px-3 py-2 rounded bg-[#131313] text-white text-sm border border-[#444] placeholder-gray-500 focus:outline-none"
                />
              </div>
              <div className="w-full sm:w-1/2">
                <label className="text-gray-400 text-sm block mb-1">Time Period</label>
                <select
                  value={timePeriod}
                  onChange={(e) => setTimePeriod(e.target.value)}
                  className="w-full px-3 py-2 rounded bg-[#131313] text-white text-sm border border-[#444] focus:outline-none"
                >
                  <option value="Monthly">Monthly</option>
                  <option value="Quarterly">Quarterly</option>
                  <option value="Yearly">Yearly</option>
                </select>
              </div>
            </div>

            <div className="flex justify-between">
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="bg-[#2b2b2b] text-white px-4 py-2 rounded text-sm border border-[#444]"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={!sourceName || !amount}
                className="bg-yellow-400 text-black px-4 py-2 rounded text-sm font-semibold disabled:opacity-50"
              >
                {editingItem ? "Update" : "Confirm"} ✓
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FixedCosts;